/* Sijara Terrace: production port of animation/index.html. */
(() => {
  if (customElements.get("sijara-terrace")) return;
  function initialize(root) {
    const gsap = window.gsap;
    const assets = JSON.parse(root.querySelector("[data-terrace-assets]").textContent);
    const getElement = id => root.querySelector(`#${id}`);
    const listeners = [];
    let resizeObserver = null, pendingRequest = null;
    function listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      listeners.push([target, type, handler, options]);
    }
/* =======================================================================
   1. LAYER CONFIGURATION  ← the only place you need to edit for media.
   -----------------------------------------------------------------------
   Coordinates are in REFERENCE PX: the 1112 × 834 frame of the reference
   video, origin top-left. (The supplied assets extend a little past the
   frame, hence the negative x/y on the base layers — that margin fills
   wide screens without zooming.)

   Per layer:
     id        human-readable name, also the element id
     src       image file — change this one value to replace the media
     x, y      top-left position in reference px
     width     rendered width in reference px (height follows the image's aspect)
     scale     size multiplier around the layer's centre (1 = as placed)
     rotation  degrees
     opacity   0–1, independent per layer
     zIndex    stacking order (higher = in front)
     visible   false hides the layer without deleting it
     depth     pointer-parallax amount, reference px at full pointer travel
               (negative = far/background, positive = near/foreground, 0 = still)
     pivot     (optional) transform origin for ambient animation, e.g. "50% 0%"

   To add a layer: drop the file in assets/, add ONE object here, done.

   Stacking order (back → front), as specified:
   reference/background → wall → vase & lamp → pillar → top branch → bottom bushes
   ======================================================================= */
const LAYERS = [
  { id: "scene-base", name: "Scene base (terrace, arches, floor)", src: "assets/scene-base.webp",
    x: -79,   y: -7,    width: 1272,  scale: 1, rotation: 0, opacity: 1, zIndex: 0, visible: true, depth: 0 },

  { id: "sky-sea",    name: "Sky, sea & hills",                    src: "assets/sky-sea.webp",
    x: -79,   y: -7,    width: 333.3, scale: 1, rotation: 0, opacity: 1, zIndex: 1, visible: true, depth: -4.5 },

  { id: "wall-panel", name: "Wall panel",                          src: "assets/wall-panel.webp",
    x: 254.4, y: -7,    width: 577.5, scale: 1, rotation: 0, opacity: 1, zIndex: 2, visible: true, depth: -1.3 },

  { id: "vase",       name: "Vase in the archway",                 src: "assets/vase.webp",
    x: 923.5, y: 379.7, width: 118.3, scale: 1, rotation: 0, opacity: 1, zIndex: 3, visible: true, depth: 1.9 },

  { id: "lantern",    name: "Hanging lantern",                     src: "assets/lantern.webp",
    x: 983.3, y: -7,    width: 57.2,  scale: 1, rotation: 0, opacity: 1, zIndex: 4, visible: true, depth: 3.2, pivot: "50% 0%" },

  // Stone pillar (right-hand archway edge) — sits between the vase/lamp tier and
  // the foreground foliage, per the requested order. Position/size derived from the
  // supplied asset's alpha bounds via the same asset-px → reference-px crop formula
  // used for every other layer (see the coordinate-system note above).
  { id: "pillar",     name: "Stone pillar (right archway)",        src: "assets/pillar.webp",
    x: 1019.2, y: -7,   width: 174.6, scale: 1, rotation: 0, opacity: 1, zIndex: 5, visible: true, depth: 2.0 },

  // The wall copy is text (written in the HTML above), so it has no src —
  // but it is placed, scaled, faded, stacked and hidden exactly like an image layer.
  { id: "wall-copy",  name: "Wall copy (text)",                    src: null,
    x: 296,   y: 98,    width: 496,   scale: 1, rotation: 0, opacity: 1, zIndex: 6, visible: true, depth: 0 },

  // Foreground framing: separate foliage plates with reference-matched SVG deformation.
  { id: "blossom-tl", name: "Blossom branch (top-left)",           src: "assets/blossom-tl.webp",
    x: -78.9, y: -7,    width: 530.4, scale: 1, rotation: 0, opacity: 1, blur: 0, zIndex: 7, visible: true, depth: 2.4,
    foliageFilter: "foliage-breeze-top" },

  { id: "bush-bl",    name: "Lavender bush (bottom-left)",         src: "assets/bush-bl.webp",
    x: -78.9, y: 438.8, width: 293.8, scale: 1, rotation: 0, opacity: 1, blur: 0, zIndex: 7, visible: true, depth: 2.4,
    foliageFilter: "foliage-breeze-left" },

  { id: "bush-br",    name: "Lavender bush (bottom-right)",        src: "assets/bush-br.webp",
    x: 837.0, y: 498.3, width: 354.9, scale: 1, rotation: 0, opacity: 1, blur: 0, zIndex: 7, visible: true, depth: 2.4,
    foliageFilter: "foliage-breeze-right" },
];
LAYERS.forEach(layer => { if (layer.src) layer.src = assets[layer.src.split("/").pop()]; });

/* Reference canvas, the extent of the assets around it, and the visual focal
   point that must stay on the viewport centre (the reference's own centre). */
const REF   = { w: 1112, h: 834 };
const EXT   = { x: -79, y: -7, w: 1272, h: 848 };
const FOCAL = { x: 556, y: 417 };
const COVER_OVERSCAN = 1.002; // prevents fractional-pixel seams on high-DPI/4K viewports

/* Layout: the one control for spacing between the title block and the subtext line. */
const LAYOUT = {
  titleGroupY: 20,        // reference px: moves the monogram, headline and rule together
  titleSubtextGap: 105,   // reference px — distance from the ornamental rule to "We're crafting…"
};

/* Motion settings */
const MOTION = {
  sceneParallaxStrength: 2.15,  // multiplier for all image-layer depth values
  textParallax: 0.75,           // 0–1; the "Text mouse movement" slider
  textParallaxMax: { x: 9, y: 5.8 }, // reference px at 100%
  parallaxEase: { duration: .72, ease: "power3.out" },
};

/* =======================================================================
   2. AUDIO CONFIGURATION  ← the only place you need to edit for the soundtrack.
   -----------------------------------------------------------------------
   Replace `src` with a real file (kept in assets/) to change the music —
   nothing else in the page references the audio path. The demo track here
   is a synthesized, seamlessly-looping ambient pad (no external license).
   ======================================================================= */
const audioConfig = {
  src: assets["demo-ambient.mp3"],
  loop: true,
  volume: 0.35,
  visualizerEnabled: true,
  visualizerBars: 24,
  visualizerSpeed: 1,       // multiplier on how fast bars chase the target level
  visualizerIntensity: 0.6, // multiplier on how tall bars react to the audio
};
root.querySelectorAll("filter[id]").forEach(filter => { filter.id = `${root.id}-${filter.id}`; });
LAYERS.forEach(layer => { if (layer.foliageFilter) layer.foliageFilter = `${root.id}-${layer.foliageFilter}`; });
const motionPreference = matchMedia("(prefers-reduced-motion: reduce)");
const reduceMotion = motionPreference.matches;

/* =======================================================================
   3. LAYER SYSTEM — builds and updates the DOM from LAYERS
   ======================================================================= */
const scene   = getElement("scene");
const LIGHT_Z = LAYERS.length + 10;   // light overlay always sits on top

function createLayer(layer) {
  if (!layer.src) return getElement(layer.id);     // text layer already in the HTML
  const el = document.createElement("div");
  el.className = "layer";
  el.id = layer.id;
  const img = document.createElement("img");
  img.src = layer.src; img.alt = ""; img.decoding = "async";
  el.appendChild(img);
  scene.appendChild(el);
  return el;
}

/* Push a layer's current config values to its element. Call after any edit. */
function applyLayer(layer) {
  const el = layer.el;
  el.style.left = layer.x + "px";
  el.style.top = layer.y + "px";
  el.style.width = layer.width + "px";
  el.style.opacity = layer.opacity;      // independent per layer — never a shared/global value
  el.style.zIndex = layer.zIndex;
  el.hidden = !layer.visible;
  const image = layer.src ? el.querySelector("img") : null;
  if (image) {
    const filters = [];
    if (layer.foliageFilter && !motionPreference.matches) filters.push(`url(#${layer.foliageFilter})`);
    if (layer.blur) filters.push(`blur(${layer.blur}px)`);
    image.style.filter = filters.length ? filters.join(" ") : "none";
  }
  gsap.set(el, { scale: layer.scale, rotation: layer.rotation });
}

LAYERS.forEach(layer => { layer.el = createLayer(layer); applyLayer(layer); });
listen(motionPreference, "change", () => LAYERS.forEach(applyLayer));
getElement("light").style.zIndex = LIGHT_Z;

function applyLayout() {
  root.style.setProperty("--title-group-y", LAYOUT.titleGroupY + "px");
  root.style.setProperty("--title-gap", LAYOUT.titleSubtextGap + "px");
}

/* =======================================================================
   4. RESPONSIVE SCENE — the viewport is a window looking at the canvas
   -----------------------------------------------------------------------
   scale    = smallest scale at which the asset canvas still fills the window
   position = FOCAL on the window centre, clamped so we never see past the
              canvas edge. Outer margins crop; the centre never moves.
   ======================================================================= */
const viewport = getElement("viewport");
gsap.set(scene, { transformOrigin: "0 0" });

function frameScene() {
  const vw = viewport.clientWidth, vh = viewport.clientHeight;
  const s = Math.max(vw / EXT.w, vh / EXT.h) * COVER_OVERSCAN;
  const isCompact = vw <= 749;
  const portraitTypeBoost = vw <= 480 && vh > vw ? Math.min(1.35, Math.max(1, .9 / s)) : 1;
  scene.style.setProperty("--headline-size", `${27.3 * portraitTypeBoost}px`);
  scene.style.setProperty("--body-size", `${17.8 * portraitTypeBoost}px`);
  scene.style.setProperty("--tuned-size", `${14.6 * portraitTypeBoost}px`);
  scene.style.setProperty("--compact-gap-reduction", `${(portraitTypeBoost - 1) * 240}px`);
  let x = vw / 2 - FOCAL.x * s;
  let y = vh / 2 - FOCAL.y * s;
  x = Math.min(-EXT.x * s, Math.max(vw - (EXT.x + EXT.w) * s, x));
  y = Math.min(-EXT.y * s, Math.max(vh - (EXT.y + EXT.h) * s, y));
  gsap.set(scene, { x, y, scale: s });
  root.style.setProperty("--signup-top", `${y + 584 * s}px`);
}
let frameRaf = 0;
const requestFrame = () => { cancelAnimationFrame(frameRaf); frameRaf = requestAnimationFrame(frameScene); };
frameScene();
listen(window, "resize", requestFrame);
listen(window, "orientationchange", requestFrame);
if ("ResizeObserver" in window) (resizeObserver = new ResizeObserver(requestFrame)).observe(viewport);

/* =======================================================================
   5. POINTER PARALLAX (GSAP)
   Image layers move on their inner <img>, the text layer on itself, so the
   editable transform from applyLayer() and the motion never collide.
   ======================================================================= */
const finePointer  = matchMedia("(hover: hover) and (pointer: fine)").matches;
const pointer = { x: 0, y: 0 };   // -1..1

const movers = LAYERS.map(layer => {
  const target = layer.src ? layer.el.querySelector("img") : layer.el;
  return { layer, x: gsap.quickTo(target, "x", MOTION.parallaxEase), y: gsap.quickTo(target, "y", MOTION.parallaxEase) };
});

function applyParallax() {
  movers.forEach(m => {
    const isText = !m.layer.src;
    const dx = isText ? MOTION.textParallaxMax.x * MOTION.textParallax : m.layer.depth * MOTION.sceneParallaxStrength;
    const dy = isText ? MOTION.textParallaxMax.y * MOTION.textParallax : m.layer.depth * MOTION.sceneParallaxStrength * .6;
    m.x(pointer.x * dx); m.y(pointer.y * dy);
  });
}
function setPointer(x, y) {
  pointer.x = Math.max(-1, Math.min(1, x)); pointer.y = Math.max(-1, Math.min(1, y));
  applyParallax();
}
if (!reduceMotion) {
  if (finePointer) {
    listen(viewport, "pointermove", e => {
      if (e.pointerType && e.pointerType !== "mouse") return;
      setPointer((e.clientX / innerWidth - .5) * 2, (e.clientY / innerHeight - .5) * 2);
    });
    listen(viewport, "pointerleave", () => setPointer(0, 0));
  } else {
    // Touch devices: gentle tilt instead of the mouse, where the browser allows it.
    listen(window, "deviceorientation", e => {
      if (e.gamma == null || e.beta == null) return;
      setPointer(e.gamma / 25, (e.beta - 40) / 25);
    });
  }
}

/* =======================================================================
   6. AMBIENT ANIMATION (GSAP)
   ======================================================================= */
if (!reduceMotion) {
  gsap.from(root.querySelectorAll("#titleGroup > *, #wall-copy > .body, #wall-copy > .star, #wall-copy > .tuned"), { opacity: 0, y: 6, duration: 1.4, stagger: .15, ease: "power3.out" });

  const lantern = LAYERS.find(l => l.id === "lantern");
  if (lantern) gsap.fromTo(lantern.el.querySelector("img"),
    { rotation: -.6, transformOrigin: lantern.pivot || "50% 50%" },
    { rotation: .6, duration: 4.5, yoyo: true, repeat: -1, ease: "sine.inOut" });

  gsap.fromTo(getElement("light"),
    { xPercent: -2, yPercent: -1, scale: 1 },
    { xPercent: 2, yPercent: 1.5, scale: 1.06, duration: 18, yoyo: true, repeat: -1, ease: "sine.inOut" });
}

/* =======================================================================
   7. AUDIO ENGINE
   Starts on the visitor's first interaction because browsers block
   unmuted autoplay, and keeps play/pause state authoritative from the <audio> element
   itself — the UI and visualizer only ever react to real playback state.
   ======================================================================= */
const audioEl = getElement("ambientAudio");
audioEl.loop = audioConfig.loop;
audioEl.volume = audioConfig.volume;
audioEl.src = audioConfig.src;

let audioCtx = null, analyser = null, freqData = null;
const canAnalyseMedia = location.protocol === "http:" || location.protocol === "https:";

function ensureAudioGraph() {
  // MediaElementAudioSource is intentionally skipped for file:// pages:
  // browsers treat local files as unique origins and otherwise log a CORS error.
  if (audioCtx || !audioConfig.visualizerEnabled || !canAnalyseMedia) return;
  try {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioCtx.createMediaElementSource(audioEl);
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 64;
    analyser.smoothingTimeConstant = .8;
    source.connect(analyser);
    analyser.connect(audioCtx.destination);
    freqData = new Uint8Array(analyser.frequencyBinCount);
  } catch (err) {
    if (audioCtx) audioCtx.close().catch(() => {});
    audioCtx = null;
    analyser = null; // Web Audio unavailable — visualizer falls back to a GSAP-driven animation (section 8)
  }
}

async function playAudio() {
  setAudioUI("loading");
  try {
    ensureAudioGraph();
    if (audioCtx && audioCtx.state === "suspended") await audioCtx.resume();
    await audioEl.play();
  } catch (error) {
    setAudioUI("paused");
    throw error;
  }
}

/* =======================================================================
   8. AUDIO VISUALIZER — built from real <div> bars, not an image.
   Reads live frequency data from the AnalyserNode when available; falls
   back to a lightweight GSAP-driven organic animation otherwise. Either
   way, motion starts and stops exactly with real playback state.
   ======================================================================= */
const visualizerEl = getElement("visualizer");
const barEls = [];
if (audioConfig.visualizerEnabled) {
  for (let i = 0; i < audioConfig.visualizerBars; i++) {
    const bar = document.createElement("div");
    bar.className = "bar";
    visualizerEl.appendChild(bar);
    barEls.push(bar);
  }
}
const barLevels = barEls.map(() => 0.12);
let visualizerRaf = 0;

function paintBars() {
  for (let i = 0; i < barEls.length; i++) barEls[i].style.transform = `scaleY(${barLevels[i]})`;
}

function stepVisualizer() {
  const speed = 0.18 * audioConfig.visualizerSpeed;
  if (analyser) {
    analyser.getByteFrequencyData(freqData);
    const bins = freqData.length;
    for (let i = 0; i < barEls.length; i++) {
      const bin = Math.floor((i / barEls.length) * bins);
      const target = Math.max(0.08, (freqData[bin] / 255) * audioConfig.visualizerIntensity);
      barLevels[i] += (target - barLevels[i]) * speed;
    }
  } else {
    // Fallback: organic, non-mechanical drift when real analysis isn't available.
    const t = performance.now() / 1000;
    for (let i = 0; i < barEls.length; i++) {
      const target = 0.15 + 0.35 * Math.abs(Math.sin(t * 1.3 + i * 0.7));
      barLevels[i] += (target - barLevels[i]) * speed;
    }
  }
  paintBars();
  visualizerRaf = requestAnimationFrame(stepVisualizer);
}

function startVisualizer() {
  if (!audioConfig.visualizerEnabled || reduceMotion) return;
  cancelAnimationFrame(visualizerRaf);
  visualizerRaf = requestAnimationFrame(stepVisualizer);
}
function calmVisualizer() {
  cancelAnimationFrame(visualizerRaf);
  if (!barEls.length) return;
  const calmLevels = barEls.map((_, i) => 0.10 + (i % 3) * 0.02);
  // Tween each bar's own transform smoothly to its calm height, then stop reacting entirely.
  barEls.forEach((bar, i) => gsap.to(bar, { scaleY: calmLevels[i], duration: .6, ease: "power2.out",
    onUpdate: () => { barLevels[i] = gsap.getProperty(bar, "scaleY"); } }));
}
function setStaticBars(isPlaying) {
  // prefers-reduced-motion path: two fixed states, no continuous animation.
  if (!barEls.length) return;
  const level = isPlaying ? 0.5 : 0.12;
  barEls.forEach((bar, i) => { bar.style.transform = `scaleY(${level + (i % 3) * 0.04})`; });
}

/* =======================================================================
   9. AUDIO CONTROL — clickable waveform, top-right; state always mirrors
   the real <audio> element so the visualizer never claims music is
   playing when it isn't.
   ======================================================================= */
const audioToggle = getElement("audioToggle");
let audioUiState = "paused";

function setAudioUI(state) {
  audioUiState = state;
  const isPlaying = state === "playing";
  const isBuffering = state === "loading" || state === "buffering";
  audioToggle.classList.toggle("is-playing", isPlaying);
  audioToggle.classList.toggle("is-buffering", isBuffering);
  audioToggle.dataset.state = state;
  audioToggle.setAttribute("aria-pressed", String(isPlaying));
  audioToggle.setAttribute("aria-busy", String(isBuffering));
  audioToggle.setAttribute("aria-label", isPlaying ? "Pause ambient sound" : isBuffering ? "Loading ambient sound" : "Play ambient sound");
  if (reduceMotion) { setStaticBars(isPlaying); return; }
  if (isPlaying) startVisualizer(); else calmVisualizer();
}
listen(audioEl, "play", () => setAudioUI("loading"));
listen(audioEl, "playing", () => setAudioUI("playing"));
listen(audioEl, "waiting", () => { if (!audioEl.paused) setAudioUI("buffering"); });
listen(audioEl, "stalled", () => { if (!audioEl.paused) setAudioUI("buffering"); });
listen(audioEl, "pause", () => setAudioUI("paused"));
listen(audioEl, "ended", () => {
  if (audioConfig.loop && root.isConnected) {
    audioEl.currentTime = 0;
    playAudio().catch(() => setAudioUI("paused"));
  } else setAudioUI("paused");
});
listen(audioEl, "emptied", () => setAudioUI("paused"));
listen(audioEl, "error", () => setAudioUI("error"));

listen(audioToggle, "click", async () => {
  removeActivationListeners();
  if (audioEl.paused) {
    try { await playAudio(); } catch (_) { setAudioUI("paused"); }
  } else audioEl.pause();
});

listen(document, "visibilitychange", () => {
  if (document.hidden) cancelAnimationFrame(visualizerRaf);
  else if (audioUiState === "playing") startVisualizer();
});

// Unmuted audio can only begin after a user gesture. Start once, on the first
// general interaction, without racing the dedicated waveform control.
const activationEvents = ["pointerdown", "keydown", "touchstart"];
function removeActivationListeners() {
  activationEvents.forEach(type => document.removeEventListener(type, startAudioOnFirstInteraction));
}
function startAudioOnFirstInteraction(event) {
  if (event.target.closest && event.target.closest("#audioToggle")) return;
  playAudio().catch(() => setAudioUI("paused"));
  removeActivationListeners();
}
activationEvents.forEach(type => listen(document, type, startAudioOnFirstInteraction, { passive: true }));


const form = root.querySelector("[data-sijara-signup]");
const emailInput = root.querySelector("[data-email-input]");
const submitButton = root.querySelector("[data-submit-button]");
const submitLabel = form.querySelector("[data-submit-label]");
const status = root.querySelector("[data-form-status]");
const defaultButtonLabel = submitLabel.textContent;
const messages = {
  loading: root.dataset.loadingLabel,
  invalidEmail: root.dataset.invalidEmailMessage,
  requiredEmail: root.dataset.requiredEmailMessage,
  unavailable: root.dataset.unavailableMessage,
  rateLimit: root.dataset.rateLimitMessage,
  rejected: root.dataset.rejectedMessage,
  error: root.dataset.errorMessage,
};
let requestPending = false, isExpanded = false;
function setExpanded(expanded) {
  isExpanded = expanded;
  form.classList.toggle("is-expanded", expanded);
  submitButton.setAttribute("aria-expanded", String(expanded));
  emailInput.setAttribute("aria-hidden", String(!expanded));
  if (expanded) {
    emailInput.removeAttribute("tabindex");
    requestAnimationFrame(() => { if (root.isConnected) emailInput.focus({ preventScroll: true }); });
  } else emailInput.setAttribute("tabindex", "-1");
}
form.classList.add("is-ready");
setExpanded(false);
    const setStatus = (message = '', state = '') => {
      status.textContent = message;
      status.dataset.state = state;
    };

    const setLoading = (isLoading) => {
      requestPending = isLoading;
      form.classList.toggle('is-loading', isLoading);
      emailInput.readOnly = isLoading;
      submitButton.disabled = isLoading;
      submitButton.setAttribute('aria-busy', String(isLoading));
      submitLabel.textContent = isLoading ? messages.loading : defaultButtonLabel;
    };

    listen(emailInput, 'input', () => {
      emailInput.removeAttribute('aria-invalid');
      if (status.dataset.state === 'error') setStatus();
    });

    listen(form, 'submit', async (event) => {
      event.preventDefault();
      if (requestPending) return;

      if (!isExpanded) {
        setExpanded(true);
        return;
      }

      const email = emailInput.value.trim().toLowerCase();
      const endpoint = form.dataset.endpoint.trim();
      emailInput.value = email;
      setStatus();

      if (!email || !emailInput.validity.valid) {
        emailInput.setAttribute('aria-invalid', 'true');
        setStatus(email ? messages.invalidEmail : messages.requiredEmail, 'error');
        emailInput.focus();
        return;
      }

      if (!/^https:\/\//i.test(endpoint) && !/^http:\/\/localhost(?::\d+)?(?:\/|$)/i.test(endpoint)) {
        setStatus(messages.unavailable, 'error');
        console.error('Sijara HubSpot API endpoint is missing or invalid.');
        return;
      }

      const controller = new AbortController();
      pendingRequest = controller;
      const timeout = window.setTimeout(() => controller.abort(), 12000);
      setLoading(true);

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            website: form.elements.website.value,
            source: 'Sijara coming soon',
            pageUri: window.location.href,
            consent: true,
          }),
          signal: controller.signal,
        });

        let result = null;
        try {
          result = await response.json();
        } catch (error) {
          result = null;
        }

        if (!response.ok || result?.ok !== true) {
          const requestError = new Error(`HubSpot contact request failed with status ${response.status}`);
          requestError.status = response.status;
          throw requestError;
        }

        emailInput.value = '';
        emailInput.removeAttribute('aria-invalid');
        setStatus(form.dataset.successMessage, 'success');
      } catch (error) {
        if (error.status === 429) {
          setStatus(messages.rateLimit, 'error');
        } else if (error.status === 400 || error.status === 422) {
          setStatus(messages.rejected, 'error');
        } else {
          setStatus(messages.error, 'error');
        }

        console.error('Sijara HubSpot contact submission error:', error);
      } finally {
        window.clearTimeout(timeout);
        pendingRequest = null;
        setLoading(false);
      }
    });

applyLayout();
setAudioUI("paused");
return () => {
  listeners.forEach(([target, type, handler, options]) => target.removeEventListener(type, handler, options));
  resizeObserver?.disconnect();
  cancelAnimationFrame(frameRaf);
  cancelAnimationFrame(visualizerRaf);
  pendingRequest?.abort();
  audioEl.pause();
  audioEl.removeAttribute("src");
  audioEl.load();
  audioCtx?.close().catch(() => {});
  gsap.killTweensOf(root.querySelectorAll("*"));
  LAYERS.forEach(layer => { if (layer.src) layer.el.remove(); });
  visualizerEl.replaceChildren();
  root.querySelectorAll("filter[id]").forEach(filter => { filter.id = filter.id.replace(`${root.id}-`, ""); });
};

  }
  customElements.define("sijara-terrace", class extends HTMLElement {
    connectedCallback() { if (!this.cleanup) this.cleanup = initialize(this); }
    disconnectedCallback() { this.cleanup?.(); this.cleanup = null; }
  });
})();
